'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import styles from './tabs.module.scss';

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cx(styles.tabs, className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cx(styles.tabsList, className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cx(styles.tabsTrigger, className)}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cx(styles.tabsContent, className)}
      {...props}
    />
  );
}

export type TabOption<T> = {
  value: T;
  label: React.ReactNode;
  content: React.ReactNode;
};

type DefaultTabsProps<T> = {
  options: TabOption<T>[];
  value: T;
  defaultValue?: T;
  onChange: (value: T) => void;
  className?: string;
};

function DefaultTabs<T extends string>({
  options,
  value,
  defaultValue,
  onChange,
  className
}: DefaultTabsProps<T>) {
  return (
    <Tabs
      value={value}
      defaultValue={defaultValue}
      onValueChange={(value) => onChange(value as T)}
      className={className}
    >
      <TabsList>
        {options.map((option) => (
          <TabsTrigger key={option.value} value={option.value}>
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {options.map((option) => (
        <TabsContent key={option.value} value={option.value}>
          <motion.div
            key={option.value}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              type: 'spring',
              damping: 15,
              mass: 0.75
            }}
          >
            {option.content}
          </motion.div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
export default DefaultTabs;
